import { Box, Button, Typography } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import { InputField } from "../../../components/Form/InputField";
import { OtpField } from "../../../components/Form/OtpField";
import OtpVerificationModal from "../../../components/Modal/OtpVerificationModal";
import * as Yup from "yup";
import {
  loginWithEmailOtp,
  loginWithOtp,
  sendOtp,
} from "../../../service/Auth/authService";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import { PARTNER_PRIMARY_HEX } from "../../../constants/partnerBrand";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

function isEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

function isMobile(value: string): boolean {
  const digits = value.trim().replace(/\D/g, "");
  return digits.length === 10 && MOBILE_REGEX.test(digits);
}

function getInputType(value: string): "email" | "phone" | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (EMAIL_REGEX.test(trimmed)) return "email";
  const digits = trimmed.replace(/\D/g, "").slice(-10);
  if (digits.length === 10 && MOBILE_REGEX.test(digits)) return "phone";
  return null;
}

const unifiedSchema = Yup.object().shape({
  emailOrPhone: Yup.string()
    .trim()
    .required("Email or mobile number is required")
    .test(
      "email-or-phone",
      "Enter a valid email or 10-digit mobile number",
      (value) => !value || isEmail(value) || isMobile(value)
    ),
  otp: Yup.string()
    .required("OTP is required")
    .matches(/^\d+$/, "Invalid OTP")
    .matches(/^\d{4}$/, "OTP must be 4 digits"),
});

export interface UnifiedLoginFormProps {
  onSuccess: (response: any) => void | Promise<void>;
  /** Styling aligned with the Tazty partner portal sign-in card. */
  partnerVisual?: boolean;
}

const UnifiedLoginForm: React.FC<UnifiedLoginFormProps> = ({ onSuccess, partnerVisual }) => {
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isFirstTimeOtp, setIsFirstTimeOtp] = useState(true);
  const [loginType, setLoginType] = useState<"email" | "phone" | null>(null);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | null>(null);
  const formikRef = useRef<FormikProps<{ emailOrPhone: string; otp: string }> | null>(null);

  useEffect(() => {
    if (showOtpModal && timer > 0 && isFirstTimeOtp) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [showOtpModal, timer, isFirstTimeOtp]);

  const buildSendOtpPayload = (value: string) => {
    const trimmed = value.trim();
    if (EMAIL_REGEX.test(trimmed)) {
      return { email: trimmed, purpose: "login" as const };
    }
    const digits = trimmed.replace(/\D/g, "").slice(-10);
    return { phone_number: digits, purpose: "login" as const };
  };

  const OTP_RETRIES_EXCEEDED_MSG = "OTP multiple retries exceeded";
  const TRY_AGAIN_AFTER_5_MIN = " Please try again after 5 minutes.";

  const getSendOtpInnerData = (response: any) =>
    response?.data?.data ?? response?.data;

  const handleGetOtp = async (
    value: string,
    setFieldValue?: (field: string, val: string) => void
  ) => {
    const type = getInputType(value);
    if (!type) {
      notifyError("Enter a valid email or 10-digit mobile number");
      return;
    }
    const payload = buildSendOtpPayload(value);
    try {
      const response = await sendOtp(payload);
      if (response.status === 200 || response.status === 201) {
        const inner = getSendOtpInnerData(response);
        const success = inner?.success ?? response.data?.success;
        const message = inner?.message ?? response.data?.message;
        if (success) {
          setLoginType(type);
          setShowOtpModal(true);
          setIsFirstTimeOtp(true);
          setTimer(30);
          notifySuccess(message || response.data?.message || "OTP sent. Enter OTP in the modal.");
        } else {
          const isRetriesExceeded = message?.toLowerCase().includes(OTP_RETRIES_EXCEEDED_MSG.toLowerCase());
          notifyError(
            isRetriesExceeded
              ? (message || OTP_RETRIES_EXCEEDED_MSG) + TRY_AGAIN_AFTER_5_MIN
              : (message || "Failed to send OTP")
          );
          if (isRetriesExceeded && setFieldValue) {
            closeOtpModalOnExpired(setFieldValue);
          }
        }
      }
    } catch (error: any) {
      notifyError(
        error?.response?.data?.message || error?.message || "Failed to send OTP"
      );
    }
  };

  const handleResendOtp = async (
    value: string,
    setFieldValue: (field: string, val: string) => void
  ) => {
    const type = getInputType(value);
    if (!type) return;
    const payload = buildSendOtpPayload(value);
    try {
      const response = await sendOtp(payload);
      if (response.status === 200 || response.status === 201) {
        const inner = getSendOtpInnerData(response);
        const success = inner?.success ?? response.data?.success;
        const message = inner?.message ?? response.data?.message;
        if (success) {
          setFieldValue("otp", "");
          setTimer(30);
          setIsFirstTimeOtp(true);
          notifySuccess(message || response.data?.message || "OTP sent successfully");
        } else {
          const isRetriesExceeded = message?.toLowerCase().includes(OTP_RETRIES_EXCEEDED_MSG.toLowerCase());
          notifyError(
            isRetriesExceeded
              ? (message || OTP_RETRIES_EXCEEDED_MSG) + TRY_AGAIN_AFTER_5_MIN
              : (message || "Failed to resend OTP")
          );
          if (isRetriesExceeded) {
            handleCloseOtpModal(setFieldValue);
          }
        }
      }
    } catch (error: any) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to resend OTP"
      );
    }
  };

  const closeOtpModalOnExpired = (setFieldValue: (field: string, value: string) => void) => {
    setFieldValue("otp", "");
    setShowOtpModal(false);
    setIsFirstTimeOtp(false);
    setLoginType(null);
    setTimer(0);
    setOtpErrorMessage(null);
  };

  const shouldCloseModalOn400 = (status: number, message?: string) => {
    if (status !== 400) return false;
    const msg = (message ?? "").toLowerCase();
    if (msg.includes("invalid otp")) return false;
    return true;
  };

  const isInvalidOtpError = (status: number, message?: string) =>
    status === 400 && (message ?? "").toLowerCase().includes("invalid otp");

  /** Get the exact message from login/verify OTP response or error for display below OTP field */
  const getResponseMessage = (responseOrError: any, fallback: string) =>
    responseOrError?.data?.message ??
    responseOrError?.data?.data?.message ??
    responseOrError?.message ??
    fallback;

  const handleInvalidOtpResponse = (
    setFieldValue: (field: string, value: string) => void,
    setFieldError: (field: string, value: string) => void,
    setFieldTouched: (field: string, touched?: boolean) => void,
    message: string
  ) => {
    const displayMessage = message || "Invalid OTP";
    setFieldValue("otp", "");
    setFieldError("otp", displayMessage);
    setFieldTouched("otp", true);
    setOtpErrorMessage(displayMessage);
  };

  const handleSubmit = async (
    values: { emailOrPhone: string; otp: string },
    setFieldValue: (field: string, value: string) => void,
    setFieldError: (field: string, value: string) => void,
    setFieldTouched: (field: string, touched?: boolean) => void
  ) => {
    if (!showOtpModal || !loginType) {
      notifyError("OTP verification is required before proceeding.");
      return;
    }
    const otp = (values.otp || "").trim();
    if (!otp || otp.length !== 4) {
      notifyError("Please enter the 4-digit OTP to continue.");
      return;
    }
    const identifier = values.emailOrPhone.trim();

    try {
      if (loginType === "email") {
        const loginResponse: any = await loginWithEmailOtp({
          email: identifier,
          otp,
        });
        if (loginResponse?.status === 200 || loginResponse?.status === 201) {
          if (loginResponse.data?.success === false) {
            notifyError(loginResponse.data?.message || "Login failed.");
            return;
          }
          await onSuccess(loginResponse);
        } else {
          const status = loginResponse?.status ?? 0;
          const apiMessage = getResponseMessage(loginResponse, "Login failed. Please try again.");
          const msgForInvalidOtp = getResponseMessage(loginResponse, "Invalid OTP");
          notifyError(apiMessage);
          if (isInvalidOtpError(status, apiMessage)) {
            handleInvalidOtpResponse(setFieldValue, setFieldError, setFieldTouched, msgForInvalidOtp);
          } else if (shouldCloseModalOn400(status, apiMessage)) {
            closeOtpModalOnExpired(setFieldValue);
          }
        }
      } else {
        const phone_number = identifier.replace(/\D/g, "").slice(-10);
        const loginResponse: any = await loginWithOtp({
          phone_number,
          otp,
        });
        if (loginResponse?.status === 200 || loginResponse?.status === 201) {
          if (loginResponse.data?.success === false) {
            notifyError(loginResponse.data?.message || "Login failed.");
            return;
          }
          await onSuccess(loginResponse);
        } else {
          const status = loginResponse?.status ?? 0;
          const apiMessage = getResponseMessage(loginResponse, "Login failed. Please try again.");
          const msgForInvalidOtp = getResponseMessage(loginResponse, "Invalid OTP");
          notifyError(apiMessage);
          if (isInvalidOtpError(status, apiMessage)) {
            handleInvalidOtpResponse(setFieldValue, setFieldError, setFieldTouched, msgForInvalidOtp);
          } else if (shouldCloseModalOn400(status, apiMessage)) {
            closeOtpModalOnExpired(setFieldValue);
          }
        }
      }
    } catch (error: any) {
      const status = error?.response?.status ?? 0;
      const apiMessage = getResponseMessage(error?.response ?? error, "Something went wrong");
      const msgForInvalidOtp = getResponseMessage(error?.response ?? error, "Invalid OTP");
      notifyError(apiMessage);
      if (isInvalidOtpError(status, apiMessage)) {
        handleInvalidOtpResponse(setFieldValue, setFieldError, setFieldTouched, msgForInvalidOtp);
      } else if (shouldCloseModalOn400(status, apiMessage)) {
        closeOtpModalOnExpired(setFieldValue);
      }
    }
  };

  const handleCloseOtpModal = (setFieldValue: (field: string, value: string) => void) => {
    setFieldValue("otp", "");
    setShowOtpModal(false);
    setIsFirstTimeOtp(false);
    setLoginType(null);
    setTimer(0);
    setOtpErrorMessage(null);
  };

  const modalSubtitle =
    loginType === "email"
      ? "We have sent you one time password to your email"
      : "We have sent you one time password to your mobile";

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{ emailOrPhone: "", otp: "" }}
      validationSchema={unifiedSchema}
      onSubmit={(values, { setFieldValue, setFieldError, setFieldTouched }) => handleSubmit(values, setFieldValue, setFieldError, setFieldTouched)}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Box sx={{ mt: partnerVisual ? 0 : { xs: 2, sm: 3 } }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: partnerVisual ? 800 : 500,
                  fontSize: partnerVisual ? "10px" : "clamp(11px, 1.4vw, 13px)",
                  mb: "8px",
                  ml: partnerVisual ? 0.5 : 0,
                  letterSpacing: partnerVisual ? "0.14em" : undefined,
                  textTransform: partnerVisual ? "uppercase" : undefined,
                  color: partnerVisual ? PARTNER_PRIMARY_HEX : undefined,
                }}
              >
                Email or Phone
              </Typography>
              <InputField
                name="emailOrPhone"
                label=""
                placeholder={
                  partnerVisual
                    ? "you@restaurant.com or mobile number"
                    : "Enter email or 10-digit mobile number"
                }
                height={partnerVisual ? "52px" : "50px"}
                onChange={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  setFieldValue("emailOrPhone", v);
                  if (showOtpModal) {
                    handleCloseOtpModal(setFieldValue);
                  }
                }}
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{
                    borderRadius: partnerVisual ? "12px" : "10px",
                    py: partnerVisual ? 1.75 : 1.5,
                    fontWeight: 700,
                    textTransform: "none",
                    ...(partnerVisual
                      ? {
                          backgroundColor: PARTNER_PRIMARY_HEX,
                          color: "#fff",
                          boxShadow: "0 10px 24px rgba(0,110,47,0.25)",
                          "&:hover": { backgroundColor: "#004b1e" },
                        }
                      : {
                          backgroundColor: "rgba(26, 26, 26, 1)",
                        }),
                  }}
                  onClick={() => handleGetOtp((values.emailOrPhone || "").trim(), setFieldValue)}
                >
                  {partnerVisual ? "Get Access Code" : "Get OTP"}
                </Button>
              </Box>

              {showOtpModal && (
                <OtpVerificationModal
                  open={showOtpModal}
                  onClose={() => handleCloseOtpModal(setFieldValue)}
                  timerSeconds={timer}
                  onResend={() =>
                    handleResendOtp(
                      (values.emailOrPhone || "").trim(),
                      setFieldValue
                    )
                  }
                  onVerifyClick={() => formikRef.current?.submitForm()}
                  title="Please Enter OTP"
                  subtitle={modalSubtitle}
                  resendLabel="Didn't receive the OTP?"
                  verifyButtonText="Verify & Continue"
                  errorMessage={otpErrorMessage}
                  partnerChrome={Boolean(partnerVisual)}
                >
                  <OtpField
                    name="otp"
                    label=""
                    hideLabel
                    variant="box"
                    length={4}
                    height={56}
                    inputBoxSize="large"
                    onChange={(e) => {
                      if ((e.target.value || "").length > 0) {
                        setOtpErrorMessage(null);
                      }
                    }}
                  />
                </OtpVerificationModal>
              )}
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default UnifiedLoginForm;
